import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useParams } from 'react-router-dom';
import { UsersThree, List, PencilSimple } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import PageMeta from '../components/PageMeta';
import CommunityLayout from '../components/community/CommunityLayout';
import CommunitySidebar from '../components/community/CommunitySidebar';
import PostCard from '../components/community/PostCard';
import SortSelector from '../components/community/SortSelector';
import SearchBar from '../components/community/SearchBar';
import { fetchTopics, fetchPosts } from '../utils/community';
import { fetchCommunityInsights } from '../utils/communityInsights';
import CommunityInsightsCard from '../components/community/CommunityInsightsCard';
import BottomSheet from '../components/BottomSheet';
import type { Topic, Post, SortOption, CommunityInsights } from '@researcher/shared';

export default function CommunityFeed() {
  const { topicSlug } = useParams<{ topicSlug?: string }>();
  const { currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const [topics, setTopics] = useState<Topic[]>([]);
  const [posts, setPosts] = useState<Post[]>([]);
  const [sort, setSort] = useState<SortOption>((searchParams.get('sort') as SortOption) || 'recent');
  const [activeTag, setActiveTagState] = useState<string | undefined>(searchParams.get('tag') || undefined);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState(false);
  const [insights, setInsights] = useState<CommunityInsights | null>(null);
  const [insightsLoading, setInsightsLoading] = useState(true);

  const currentTopic = topicSlug ? topics.find((t) => t.slug === topicSlug) : null;

  const setActiveTag = (tag: string | undefined) => {
    setActiveTagState(tag);
    if (tag) {
      setSearchParams({ tag });
    } else {
      setSearchParams({});
    }
  };

  useEffect(() => {
    fetchTopics().then(setTopics).catch(console.error);
    fetchCommunityInsights()
      .then(setInsights)
      .catch(console.error)
      .finally(() => setInsightsLoading(false));
  }, []);

  useEffect(() => {
    setLoading(true);
    setCursor(undefined);
    fetchPosts({ topic: topicSlug, sort, tag: !topicSlug ? activeTag : undefined })
      .then((data) => {
        setPosts(data.items);
        setCursor(data.nextCursor);
        setHasMore(data.hasMore);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [sort, activeTag, topicSlug]);

  const loadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const data = await fetchPosts({ topic: topicSlug, sort, tag: !topicSlug ? activeTag : undefined, cursor });
      setPosts((prev) => [...prev, ...data.items]);
      setCursor(data.nextCursor);
      setHasMore(data.hasMore);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query.toLowerCase());
  }, []);

  const handlePostDeleted = (postId: string) => {
    setPosts((prev) => prev.filter((p) => p.id !== postId));
  };

  const filteredPosts = searchQuery
    ? posts.filter(
        (p) =>
          p.title.toLowerCase().includes(searchQuery) ||
          p.body.toLowerCase().includes(searchQuery)
      )
    : posts;

  // Header content depends on whether a topic is selected
  const headerTitle = topicSlug
    ? currentTopic?.name || topicSlug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    : 'Community';
  const headerSub = topicSlug
    ? currentTopic?.description
    : 'Share experiences, find support, stay anonymous';

  return (
    <CommunityLayout>
      <PageMeta
        title="Women's Health Community"
        description="Join a supportive women's health community. Share experiences, ask questions, and connect with others navigating endometriosis, PCOS, PMDD, and more."
        path="/community"
      />
      <main className="flex-1 flex flex-col min-h-0 pt-16 pb-[72px] md:pb-0">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8 flex flex-col flex-1 min-h-0">
          <div className="flex gap-8 flex-1 min-h-0">
            {/* Sidebar - desktop */}
            <div className="hidden lg:flex lg:flex-col w-64 flex-shrink-0 min-h-0 overflow-y-auto">
              <CommunitySidebar topics={topics} />
            </div>

            {/* Main content */}
            <div className="flex-1 min-w-0 flex flex-col min-h-0">
              {/* Page header */}
              <div className="mb-4 md:mb-6 flex items-center justify-between">
                <div>
                  <h1 className="text-xl md:text-3xl lg:text-4xl font-bold text-gray-800 dark:text-gray-100 mb-0 md:mb-1">
                    {topicSlug ? (
                      headerTitle
                    ) : (
                      <span className="bg-gradient-to-r from-orange-500 to-pink-500 bg-clip-text text-transparent">{headerTitle}</span>
                    )}
                  </h1>
                  {headerSub && (
                    <p className="hidden md:block text-gray-500 dark:text-gray-400 text-sm">{headerSub}</p>
                  )}
                </div>
                {/* Mobile topics trigger */}
                <button
                  onClick={() => setMobileSidebarOpen(true)}
                  className="lg:hidden flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-100 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  aria-label="Browse topics"
                >
                  <List size={16} />
                  <span>Topics</span>
                </button>
              </div>

              {/* Search + Sort row */}
              <div className="flex items-center gap-2 mb-3 md:mb-4">
                <div className="flex-1">
                  <SearchBar onSearch={handleSearch} />
                </div>
                <SortSelector value={sort} onChange={setSort} />
              </div>

              {/* Active tag filter */}
              {!topicSlug && activeTag && (
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Tag: &quot;{activeTag}&quot;</span>
                  <button
                    onClick={() => setActiveTag(undefined)}
                    className="text-xs text-pink-600 dark:text-pink-400 hover:text-pink-700 dark:hover:text-pink-300 underline"
                  >
                    Clear
                  </button>
                </div>
              )}

              {/* Community insights */}
              <CommunityInsightsCard
                trendingThemes={
                  topicSlug
                    ? (insights?.topicInsights?.find(ti => ti.topicSlug === topicSlug)?.trendingThemes || [])
                    : (insights?.trendingThemes || [])
                }
                commonQuestions={
                  topicSlug
                    ? (insights?.topicInsights?.find(ti => ti.topicSlug === topicSlug)?.commonQuestions)
                    : undefined
                }
                loading={insightsLoading}
              />

              {/* Post feed — scrollable */}
              <div className="flex-1 min-h-0 overflow-y-auto pb-20 md:pb-0" style={{ minHeight: '60vh' }}>
                {loading && posts.length === 0 ? (
                  <div className="w-full space-y-3">
                    {[0, 1, 2].map((i) => (
                      <div key={i} className="w-full bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 sm:p-5 animate-pulse">
                        {/* Header skeleton */}
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700" />
                          <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-20" />
                          <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded-full w-16" />
                          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-10" />
                        </div>
                        {/* Title skeleton */}
                        <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-1" />
                        {/* Body skeleton */}
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-1.5" />
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3 mb-3" />
                        {/* Footer skeleton */}
                        <div className="flex items-center gap-4">
                          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-10" />
                          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-10" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : filteredPosts.length === 0 && !loading ? (
                  <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                    <UsersThree size={48} className="text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-gray-400 mb-2">
                      {searchQuery ? 'No matching posts' : topicSlug ? 'No posts in this topic yet' : 'No posts yet'}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                      {searchQuery ? 'Try a different search term' : 'Be the first to start a discussion!'}
                    </p>
                    {!searchQuery && currentUser && (
                      <Link to="/community/new" className="inline-flex px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors text-sm font-medium">
                        Create Post
                      </Link>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredPosts.map((post) => (
                      <PostCard key={post.id} post={post} onDeleted={handlePostDeleted} />
                    ))}

                    {hasMore && !searchQuery && (
                      <div className="text-center pt-4 pb-4">
                        <button
                          onClick={loadMore}
                          disabled={loadingMore}
                          className="px-6 py-2 text-sm text-pink-600 dark:text-pink-400 border border-pink-300 dark:border-pink-800 rounded-lg hover:bg-pink-50 dark:hover:bg-pink-950 transition-colors disabled:opacity-50"
                        >
                          {loadingMore ? 'Loading...' : 'Load More'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* New Post FAB — mobile only */}
      {currentUser && (
        <Link
          to="/community/new"
          className="md:hidden fixed bottom-28 right-4 z-30 w-14 h-14 flex items-center justify-center rounded-full bg-pink-500 text-white shadow-lg active:scale-95 transition-transform"
          aria-label="New post"
        >
          <PencilSimple size={24} weight="bold" />
        </Link>
      )}

      <BottomSheet open={mobileSidebarOpen} onClose={() => setMobileSidebarOpen(false)} title="Browse Community">
        <CommunitySidebar topics={topics} onClose={() => setMobileSidebarOpen(false)} />
      </BottomSheet>
    </CommunityLayout>
  );
}
